/* eslint-disable max-lines */
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Grid, LinearProgress, Paper, Skeleton } from '@mui/material';
import UnverifiedCell from 'common/components/UnverifiedCell';
import colors from 'common/constants/colors';
import { AgGridReact } from 'ag-grid-react';
import { groupBy, isNil, sumBy } from 'lodash';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthorizedFacilities } from 'store/selectors/facilitySelectors';
import { inventoryDashboardActions } from 'store/slices/inventoryDashboardSlice';
import loadInventoryItems from 'store/thunks/inventoryDashboardThunk';
import { getStateDisplayName } from 'store/utils/shipmentUtils';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import { useInventoryAlert } from '../context/InventoryAlertContext';
import { dashIfEmpty } from '../utils/columnFormatters';
import EditDefaultNdcDialog from './EditDefaultNdcDialog';
import AppliedFiltersHeader from './AppliedFiltersHeader';
import LotSummaryDrawer from './LotSummaryDrawer';
import ManualAdjustLotSelectorDialog from './ManualAdjustLotSelectorDialog';
import ManualAdjustmentButton from './ManualAdjustmentButton';
import ManualAdjustmentDialog from './ManualAdjustmentDialog';
import TransactionHistoryModal from './TransactionHistoryModal';

// Stable identity so LotSummaryDrawer's items-keyed fetch effect does not re-run every render.
const EMPTY_DRAWER_ITEMS = [];
// AG Grid's header height at this row height. Shared with the skeleton and with the refresh
// bar, which sits directly beneath the header rather than above it.
const GRID_HEADER_HEIGHT = 48;

const roundNumFmt = ({ value }) => (value == null ? '—' : Math.round(Number(value)).toString());

const NUM_CELL = { textAlign: 'right' };

const hw = (text) => Math.max(110, text.length * 7 + 52);

const ExpiringSoonCell = ({ value }) =>
  value > 0 ? (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 0.5 }}>
      <WarningAmberIcon sx={{ color: '#FFC107' }} fontSize='small' />
      <Box component='span' sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#FFC107' }}>
        {value} {value === 1 ? 'lot' : 'lots'}
      </Box>
    </Box>
  ) : null;

ExpiringSoonCell.propTypes = { value: PropTypes.number };
ExpiringSoonCell.defaultProps = { value: 0 };

const SKELETON_COLS = [
  { key: 'ndc', flex: 1.5 },
  { key: 'drug', flex: 3 },
  { key: 'qty', flex: 1 },
  { key: 'status', flex: 1.5 },
  { key: 'expiry', flex: 1.2 },
  { key: 'lots', flex: 1 },
  { key: 'txn', flex: 1 },
];
const SKELETON_ROW_KEYS = [
  'r0',
  'r1',
  'r2',
  'r3',
  'r4',
  'r5',
  'r6',
  'r7',
  'r8',
  'r9',
  'r10',
  'r11',
];

const TableSkeleton = () => (
  <Grid component={Paper} sx={{ width: '100%', overflow: 'hidden' }}>
    <Box sx={{ height: '65vh', width: '100%' }}>
      <Box
        sx={{
          backgroundColor: 'primary.main',
          height: GRID_HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          gap: 2,
        }}
      >
        {SKELETON_COLS.map(({ key, flex }) => (
          <Skeleton
            key={key}
            variant='text'
            sx={{ flex, backgroundColor: 'rgba(255,255,255,0.15)' }}
            height={18}
          />
        ))}
      </Box>
      {SKELETON_ROW_KEYS.map((rowKey) => (
        <Box
          key={rowKey}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            gap: 2,
            height: 42,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {SKELETON_COLS.map(({ key, flex }) => (
            <Skeleton key={key} variant='text' sx={{ flex }} height={14} />
          ))}
        </Box>
      ))}
    </Box>
  </Grid>
);

const STOCK_STATUS_CONFIG = {
  HEALTHY: { label: 'HEALTHY', color: '#2e7d32', bgColor: '#e8f5e9' },
  LOW_STOCK: { label: 'LOW', color: '#e65100', bgColor: '#fff3e0' },
  CRITICAL_STOCK: { label: 'CRITICAL', color: '#c62828', bgColor: '#fdecea' },
  OUT_OF_STOCK: { label: 'OUT OF STOCK', color: '#7f1d1d', bgColor: '#fde8e8' },
  // No dispensing in the last 14 days, so core could derive no LOW/CRITICAL
  // threshold for the item. Neutral grey on purpose: this means "not assessed",
  // not "fine" -- it used to arrive as a green HEALTHY chip. Do not style it as an
  // alert either; nothing is known to be wrong, we simply cannot say.
  NO_RECENT_MOVEMENT: { label: 'NO MOVEMENT', color: '#616161', bgColor: '#f5f5f5' },
};

const StockStatusChip = ({ value }) => {
  const config = STOCK_STATUS_CONFIG[value];
  if (!config) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: 1,
        borderRadius: '20px',
        border: `1px solid ${config.color}`,
        backgroundColor: config.bgColor,
        color: config.color,
        fontWeight: 700,
        fontSize: '0.65rem',
        whiteSpace: 'nowrap',
        letterSpacing: '0.03em',
      }}
    >
      {config.label}
    </Box>
  );
};

StockStatusChip.propTypes = { value: PropTypes.string };
StockStatusChip.defaultProps = { value: null };

const QTY_COLOR = { OUT_OF_STOCK: '#7f1d1d', CRITICAL_STOCK: '#c62828', LOW_STOCK: '#e65100' };

const LockedBadge = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.25,
      height: 22,
      px: 0.75,
      borderRadius: '4px',
      bgcolor: colors.lockedCellGrey,
      border: `1px solid ${colors.lockedCellBorder}`,
      flexShrink: 0,
    }}
  >
    <LockIcon sx={{ fontSize: '0.75rem', color: colors.lockedCellGreyIcon }} />
    <Box
      component='span'
      sx={{ fontSize: '0.7rem', fontWeight: 700, color: colors.lockedCellGreyIcon, lineHeight: 1 }}
    >
      LOCKED
    </Box>
  </Box>
);

const QtyCell = ({ value, data }) => {
  const color = QTY_COLOR[data?.stockStatus] ?? 'inherit';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        width: '100%',
      }}
    >
      {data?.allLocked && <LockedBadge />}
      <Box sx={{ color, fontWeight: color !== 'inherit' ? 600 : 400 }}>
        {isNil(value) ? '—' : Number(value).toString()}
      </Box>
    </Box>
  );
};

QtyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape({ stockStatus: PropTypes.string, allLocked: PropTypes.bool }),
};
QtyCell.defaultProps = { value: null, data: null };

const FacilityQtyCell = ({ value, data, pharmacyId }) => {
  const isLocked = data?.[`isLocked_${pharmacyId}`];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        width: '100%',
      }}
    >
      {isLocked && <LockedBadge />}
      <span>{isNil(value) ? '—' : Number(value).toString()}</span>
    </Box>
  );
};

FacilityQtyCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  data: PropTypes.shape({}),
  pharmacyId: PropTypes.string,
};
FacilityQtyCell.defaultProps = { value: null, data: null, pharmacyId: null };

const LotCountButton = ({ value, data, onLotsClick }) => (
  <Box
    component='button'
    onClick={() => onLotsClick?.(data)}
    sx={{
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      color: 'primary.main',
      fontWeight: 600,
      textDecoration: 'underline',
    }}
  >
    {value ?? 0}
  </Box>
);

LotCountButton.propTypes = {
  value: PropTypes.number,
  data: PropTypes.shape({}),
  onLotsClick: PropTypes.func,
};
LotCountButton.defaultProps = { value: 0, data: null, onLotsClick: null };

const ViewHistoryButton = ({ data, onHistoryClick }) => (
  <Box
    component='button'
    onClick={() => onHistoryClick?.(data)}
    sx={{
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      cursor: 'pointer',
      color: 'primary.main',
      fontWeight: 600,
      textDecoration: 'underline',
    }}
  >
    View
  </Box>
);

ViewHistoryButton.propTypes = {
  data: PropTypes.shape({}),
  onHistoryClick: PropTypes.func,
};
ViewHistoryButton.defaultProps = { data: null, onHistoryClick: null };

const DefaultNdcChip = () => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.25,
      height: 18,
      px: 0.75,
      borderRadius: '4px',
      bgcolor: '#FEF3C7',
      border: '1px solid #FCD34D',
      color: '#92400E',
      fontSize: '0.6rem',
      fontWeight: 700,
      letterSpacing: '0.03em',
      flexShrink: 0,
    }}
  >
    <StarIcon sx={{ fontSize: '0.7rem' }} />
    DEFAULT
  </Box>
);

const NdcCell = ({ value, data }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      height: '100%',
      fontFamily: 'monospace',
      fontSize: 13,
    }}
  >
    <span>{value}</span>
    {data?.isDefault && <DefaultNdcChip />}
  </Box>
);

NdcCell.propTypes = {
  value: PropTypes.string,
  data: PropTypes.shape({ isDefault: PropTypes.bool }),
};
NdcCell.defaultProps = { value: '', data: null };

const PRE_PHARMACY_COLS = [
  {
    field: 'ndc',
    headerName: 'NDC',
    minWidth: 160,
    pinned: 'left',
    sortable: true,
    cellRenderer: NdcCell,
  },
  {
    field: 'drugName',
    headerName: 'Drug Name',
    flex: 2,
    minWidth: 200,
    sortable: true,
    initialSort: 'asc',
  },
  {
    field: 'productId',
    headerName: 'Product ID',
    minWidth: 120,
    sortable: true,
    cellStyle: { fontFamily: 'monospace', fontSize: 13 },
    valueFormatter: ({ value }) => value || '—',
  },
  { field: 'strength', headerName: 'Strength', minWidth: 90, sortable: true },
  {
    field: 'manufacturer',
    headerName: 'Manufacturer',
    minWidth: 140,
    sortable: true,
    valueFormatter: dashIfEmpty,
  },
  {
    field: 'totalQtyOnHand',
    headerName: 'Total Qty',
    minWidth: 50,
    cellStyle: NUM_CELL,
    sortable: true,
    cellRenderer: QtyCell,
  },
  {
    field: 'stockStatus',
    headerName: 'Stock Status',
    minWidth: 100,
    cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    cellRenderer: StockStatusChip,
  },
  {
    field: 'expiringSoonCount',
    headerName: 'Expiring Soon',
    minWidth: 100,
    cellRenderer: ExpiringSoonCell,
  },
  {
    field: 'totalUnverifiedQty',
    headerName: SYSTEM_DEFICIT,
    minWidth: 100,
    sortable: true,
    // A number filter, not the grid's default text one: the question this column exists to
    // answer is "greater than 0", and a text filter cannot ask it.
    filter: 'agNumberColumnFilter',
    cellRenderer: UnverifiedCell,
  },
];

// POM-6230 — the four "since count" columns (Received Since, Dispensed Since, Last Count
// Total, Last Count Date, plus the per-facility Last Count below) used to live here. The
// fields behind them are written as zero when an inventory item row is first created and
// are never updated again, by a physical count or by anything else, so the columns could
// only ever show zeros that read as real data. `hide: true` was not enough: the grid's
// columns tool panel let anyone switch them back on. Do not re-add them until something
// maintains the underlying fields.
const POST_PHARMACY_COLS = [
  {
    field: 'isPhaseOut',
    headerName: 'Phase Out',
    minWidth: hw('Phase Out'),
    valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
    hide: true,
  },
];

// Ranks the per-facility rows so a grouped drug row can show the worst one. The
// reduce below seeds with HEALTHY and scores anything unrecognised -1, so a status
// missing from this map loses to the seed and the group silently renders green --
// which is exactly the bug NO_RECENT_MOVEMENT exists to fix. It ranks above
// HEALTHY: a drug with even one unassessed facility should not read as fine.
const STATUS_RANK = {
  OUT_OF_STOCK: 4,
  CRITICAL_STOCK: 3,
  LOW_STOCK: 2,
  NO_RECENT_MOVEMENT: 1,
  HEALTHY: 0,
};

const PharmacyInnerHeader = ({ displayName, stateName }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
    <Box component='span'>{displayName}</Box>
    {stateName ? (
      <Box component='span' sx={{ fontSize: '0.65rem', fontWeight: 400, opacity: 0.8 }}>
        ({stateName})
      </Box>
    ) : null}
  </Box>
);

PharmacyInnerHeader.propTypes = {
  displayName: PropTypes.string,
  stateName: PropTypes.string,
};
PharmacyInnerHeader.defaultProps = { displayName: '', stateName: null };

const buildPharmacyCols = (facilities) =>
  facilities.map((p) => {
    const qtyHeader = `${p.name} Qty`;
    return {
      field: `qty_${p.fulfillingPharmacyId}`,
      headerName: qtyHeader,
      headerComponentParams: {
        innerHeaderComponent: PharmacyInnerHeader,
        stateName: p.state ? getStateDisplayName(p.state) : null,
      },
      minWidth: hw(qtyHeader),
      cellStyle: ({ data }) =>
        data?.[`isLocked_${p.fulfillingPharmacyId}`]
          ? { ...NUM_CELL, backgroundColor: colors.lockedCellGrey }
          : NUM_CELL,
      cellRenderer: FacilityQtyCell,
      cellRendererParams: { pharmacyId: p.fulfillingPharmacyId },
    };
  });

const buildPivotRows = (items, facilities) => {
  const grouped = groupBy(items, 'ndc');
  return Object.values(grouped).map((rows) => {
    const first = rows[0];
    const row = {
      ndc: first.ndc,
      productId: first.productId,
      isDefault: rows.some((r) => r.isDefault),
      drugName: first.drugName,
      strength: first.strength,
      // POM-5910 — safe to read off `first`, same as drugName/strength/productId
      // above. These rows are grouped by NDC (not by product), and core resolves
      // manufacturer from the inventory master keyed by NDC, which is unique —
      // so every row within a group carries an identical manufacturer. A product
      // whose NDCs span several manufacturers therefore appears as one row per
      // NDC, each showing its own; no value is ever hidden by this pick.
      manufacturer: first.manufacturer,
      totalQtyOnHand: sumBy(rows, (r) => Number.parseFloat(r.quantityOnHand) || 0),
      totalLotCount: sumBy(rows, (r) => r.lotCount ?? 0),
      inventoryItems: rows,
      isPhaseOut: rows.some((r) => r.isPhaseOut),
      allLocked: rows.length > 0 && rows.every((r) => r.isLockForCount),
      isExpiringSoon: rows.some((r) => r.isExpiringSoon),
      expiringSoonCount: sumBy(rows, (r) => r.expiringSoonCount ?? 0),
      // POM-6087 - summed across the facilities holding this NDC, exactly as the quantities
      // above are. Kept out of totalQtyOnHand: on-hand is deliberately untouched by a claim,
      // so folding it in would state a quantity the pharmacy never held.
      totalUnverifiedQty: sumBy(rows, (r) => Number(r.unverifiedQty) || 0),
      hasUnverifiedClaim: rows.some((r) => r.hasUnverifiedClaim),
      stockStatus: rows.reduce(
        (worst, r) =>
          (STATUS_RANK[r.stockStatus] ?? -1) > (STATUS_RANK[worst] ?? -1) ? r.stockStatus : worst,
        'HEALTHY'
      ),
      totalDispensed14d: rows.some((r) => r.totalDispensed14d != null)
        ? sumBy(rows, (r) => Number(r.totalDispensed14d) || 0)
        : null,
      avgDailyDispense: rows.some((r) => r.totalDispensed14d != null)
        ? Math.round(sumBy(rows, (r) => Number(r.totalDispensed14d) || 0) / 14)
        : null,
    };
    facilities.forEach((p) => {
      const match = rows.find(
        (r) => r.fulfillingPharmacy?.fulfillingPharmacyId === p.fulfillingPharmacyId
      );
      row[`qty_${p.fulfillingPharmacyId}`] = match ? Number.parseFloat(match.quantityOnHand) : null;
      row[`isLocked_${p.fulfillingPharmacyId}`] = match?.isLockForCount ?? false;
    });
    return row;
  });
};

const getRowStyle = ({ data }) => {
  if (data?.stockStatus === 'OUT_OF_STOCK') {
    return { backgroundColor: '#FEE2E2' };
  }
  if (data?.stockStatus === 'CRITICAL_STOCK') {
    return { backgroundColor: '#FEF2F2' };
  }
  if (data?.stockStatus === 'LOW_STOCK') {
    return { backgroundColor: '#FFFBEB' };
  }
  return undefined;
};

const defaultColDef = {
  resizable: true,
  filter: 'agTextColumnFilter',
  suppressMovable: false,
  suppressHeaderMenuButton: true,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

const sideBar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
      },
    },
    'filters',
  ],
};

const InventoryDashboardTable = ({ gridRef }) => {
  const dispatch = useDispatch();
  const { state: alertState } = useInventoryAlert();
  const { items, loading } = useSelector(({ inventory }) => inventory.inventoryDashboard);
  // The user's own facilities. Every pivot column here is a facility, so reading the tenant's
  // list gave a column per facility in the tenant, holdings included (POM-6274).
  const facilities = useSelector(selectAuthorizedFacilities);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedTxRow, setSelectedTxRow] = useState(null);
  const [selectorDialog, setSelectorDialog] = useState(null);
  const [adjustDialog, setAdjustDialog] = useState(null);
  const [editDefaultRow, setEditDefaultRow] = useState(null);
  const [lotsRefreshKey, setLotsRefreshKey] = useState(0);

  const handleHistoryClick = useCallback((data) => {
    setSelectedTxRow(data);
  }, []);

  const handleEditDefaultClick = useCallback((data) => {
    setEditDefaultRow(data);
  }, []);

  // Tracks whether an adjustment was actually saved during this selector session,
  // so closing the selector only refetches the list when something changed — a
  // plain open/close no longer triggers a needless reload.
  const adjustmentMadeRef = useRef(false);

  const handleAdjustClick = useCallback((data) => {
    adjustmentMadeRef.current = false;
    const facilityOptions = (data.inventoryItems ?? []).map((r) => ({
      fulfillingPharmacyId: r.fulfillingPharmacy.fulfillingPharmacyId,
      inventoryItemId: r.inventoryItemId,
      name: r.fulfillingPharmacy.name,
      state: r.fulfillingPharmacy.state,
      isLocked: r.isLockForCount ?? false,
    }));
    setSelectorDialog({
      ndc: data.ndc,
      drugName: data.drugName,
      manufacturer: data.manufacturer,
      strength: data.strength,
      facilityOptions,
    });
  }, []);

  const handleLotSelect = useCallback(
    (lot, fulfillingPharmacyId, facilityName) => {
      setAdjustDialog({
        lot,
        ndc: selectorDialog.ndc,
        drugName: selectorDialog.drugName,
        strength: selectorDialog.strength,
        fulfillingPharmacyId,
        facilityName,
      });
      // Selector stays open behind the adjustment dialog
    },
    [selectorDialog]
  );

  const handleAdjusted = useCallback(() => {
    adjustmentMadeRef.current = true;
    setAdjustDialog(null);
    setLotsRefreshKey((k) => k + 1);
    // Table refresh deferred to selector close — avoids unmounting the selector during loading
  }, []);

  const handleSelectorClose = useCallback(() => {
    setSelectorDialog(null);
    // Only reload the list if an adjustment was actually made in this session.
    if (adjustmentMadeRef.current) {
      adjustmentMadeRef.current = false;
      dispatch(loadInventoryItems());
    }
  }, [dispatch]);

  // The Lot Summary drawer gets the same treatment as the selector above: it reloads its own lots
  // on an adjustment and the list waits until the drawer is closed, so the operator is not watching
  // a panel reload underneath them for a row the list is only going to restate.
  const lotSummaryAdjustedRef = useRef(false);

  const handleLotSummaryAdjusted = useCallback(() => {
    lotSummaryAdjustedRef.current = true;
  }, []);

  const handleLotSummaryClose = useCallback(() => {
    setSelectedRow(null);
    if (lotSummaryAdjustedRef.current) {
      lotSummaryAdjustedRef.current = false;
      dispatch(loadInventoryItems());
    }
  }, [dispatch]);

  /*
   * Mount only. These rows live in the store, which outlives the component, so All Facilities came
   * back on whatever a previous visit had left there. The facility table starts empty because its
   * items are local state; this is what makes the two agree.
   *
   * Kept apart from the load below, which also runs on summaryRefreshKey — that is a refresh, and
   * refreshes keep the grid and its column state (POM-6140).
   */
  useEffect(() => {
    dispatch(inventoryDashboardActions.resetState());
  }, [dispatch]);

  useEffect(() => {
    dispatch(loadInventoryItems());
  }, [dispatch, alertState.summaryRefreshKey]);

  const columnDefs = useMemo(
    () => [
      ...PRE_PHARMACY_COLS,
      {
        field: 'totalLotCount',
        headerName: 'Available Lots',
        minWidth: 80,
        cellRenderer: LotCountButton,
        cellRendererParams: { onLotsClick: setSelectedRow },
      },
      {
        field: 'inventoryItems',
        headerName: 'Transactions',
        minWidth: 60,
        sortable: false,
        filter: false,
        cellRenderer: ViewHistoryButton,
        cellRendererParams: { onHistoryClick: handleHistoryClick },
      },
      ...buildPharmacyCols(facilities),
      ...POST_PHARMACY_COLS,
      {
        field: 'avgDailyDispense',
        headerName: 'Avg Daily Dispense',
        minWidth: 80,
        cellStyle: NUM_CELL,
        valueFormatter: roundNumFmt,
      },
      {
        field: 'totalDispensed14d',
        headerName: 'Dispensed (14d)',
        minWidth: 80,
        cellStyle: NUM_CELL,
        valueFormatter: roundNumFmt,
      },
      {
        field: 'ndc',
        colId: 'manualAdjustActions',
        headerName: '',
        // The one header cell with nothing in it, and the only spot for a grid-wide control that
        // does not cost the banner above a row of height.
        headerComponent: AppliedFiltersHeader,
        headerClass: 'inventory-actions-header',
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        sortable: false,
        filter: false,
        resizable: false,
        pinned: 'right',
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
        cellRenderer: ManualAdjustmentButton,
        cellRendererParams: {
          onAdjustClick: handleAdjustClick,
          onEditDefaultClick: handleEditDefaultClick,
        },
      },
    ],
    [facilities, handleHistoryClick, handleAdjustClick, handleEditDefaultClick]
  );

  const rowData = useMemo(
    () => (items ? buildPivotRows(items, facilities) : []),
    [items, facilities]
  );

  // Only the first load blanks the grid. A refresh keeps it mounted, because unmounting is what
  // discarded the column filter, the sort, the column widths and the open Filters panel — none of
  // which the grid can restore afterwards (POM-6140). It is also what tore down the Lot Summary
  // drawer mid-adjustment, which is why the selector flow above already defers its reload.
  if (isNil(items)) return <TableSkeleton />;

  return (
    <>
      <LotSummaryDrawer
        items={selectedRow ? selectedRow.inventoryItems : EMPTY_DRAWER_ITEMS}
        onClose={handleLotSummaryClose}
        onAdjusted={handleLotSummaryAdjusted}
      />

      <TransactionHistoryModal
        open={Boolean(selectedTxRow)}
        ndc={selectedTxRow?.ndc}
        drugName={selectedTxRow?.drugName}
        manufacturer={selectedTxRow?.manufacturer}
        onClose={() => setSelectedTxRow(null)}
      />

      <ManualAdjustLotSelectorDialog
        open={Boolean(selectorDialog)}
        ndc={selectorDialog?.ndc}
        drugName={selectorDialog?.drugName}
        manufacturer={selectorDialog?.manufacturer}
        facilityOptions={selectorDialog?.facilityOptions ?? []}
        lotsRefreshKey={lotsRefreshKey}
        onLotSelect={handleLotSelect}
        onClose={handleSelectorClose}
      />

      <ManualAdjustmentDialog
        open={Boolean(adjustDialog)}
        lot={adjustDialog?.lot ?? null}
        drugName={adjustDialog?.drugName ?? ''}
        strength={adjustDialog?.strength ?? ''}
        ndc={adjustDialog?.ndc ?? ''}
        fulfillingPharmacyId={adjustDialog?.fulfillingPharmacyId ?? ''}
        facilityName={adjustDialog?.facilityName ?? ''}
        onSaveSuccess={handleAdjusted}
        onClose={() => setAdjustDialog(null)}
      />

      <EditDefaultNdcDialog
        open={Boolean(editDefaultRow)}
        productId={editDefaultRow?.productId ?? null}
        onSaved={() => dispatch(loadInventoryItems())}
        onClose={() => setEditDefaultRow(null)}
      />

      <Grid component={Paper} sx={{ width: '100%', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'relative',
            height: '65vh',
            width: '100%',
            '& .ag-header': { backgroundColor: 'primary.main' },
            '& .ag-header-cell-text': { color: 'primary.contrastText', fontWeight: 600 },
            '& .ag-header-icon': { color: 'primary.contrastText' },
            '& .ag-header-cell-resize::after': {
              backgroundColor: colors.tableHeaderSeparator,
              width: '2px',
            },
            '& .ag-header-cell.inventory-actions-header': {
              borderLeft: `2px solid ${colors.tableHeaderSeparator}`,
              // The control plus its badge needs the full 50px the column has.
              padding: 0,
              justifyContent: 'center',
            },
            // AG Grid wraps a custom header in its own flex box, which defaults to the start.
            // Centring only the cell leaves the control sitting against the left edge.
            '& .ag-header-cell.inventory-actions-header .ag-header-cell-comp-wrapper': {
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            },
          }}
        >
          {/* A refresh no longer blanks the grid, so it needs its own sign that something is
              happening. Under the header and over the rows, which are the ones about to change. */}
          {loading && (
            <LinearProgress
              sx={{ position: 'absolute', top: GRID_HEADER_HEIGHT, left: 0, right: 0, zIndex: 1 }}
            />
          )}
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            sideBar={sideBar}
            enableCellTextSelection
            tooltipShowDelay={400}
            getRowStyle={getRowStyle}
          />
        </Box>
      </Grid>
    </>
  );
};

InventoryDashboardTable.propTypes = {
  gridRef: PropTypes.shape({ current: PropTypes.instanceOf(Object) }).isRequired,
};

export default InventoryDashboardTable;
